import { Icons } from '@/utils/Icons';
import { useState, useEffect } from 'react';
import { FaSearch } from 'react-icons/fa';
import { Link, NavLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  CompetitionItem,
  useGetCompetitionsEventsMarketsQuery,
  useGetEventCountQuery,
} from '@/store/api/competitionApi';
import { useGetSportsMetaDetailsQuery } from '@/store/api/sportsApi';
import { useAppSelector } from '@/hooks';
import {
  useGetCasinoGamesCountQuery,
  useGetFavouriteCasinoGamesQuery,
} from '@/store/api/casinoApi';
import { useUserInfo } from '@/hooks/useUserInfo';

const providers = [
  { name: 'Asia Gaming' },
  { name: 'BetGames' },
  { name: 'Betsoft' },
  { name: 'Evoplay Entertainment' },
  { name: 'Evolution Gaming' },
  { name: 'Ezugi' },
  { name: 'Gamzix' },
  { name: 'JDB Gaming' },
  { name: 'JiLi' },
  { name: 'KINGMAKER' },
  { name: 'Kingmidas Gaming' },
  { name: 'Mac88' },
  { name: 'Playtech' },
  { name: 'Royal Gaming' },
  { name: 'Smartsoft Gaming' },
  { name: 'Spribe' },
  { name: 'Turbo Games' },
  { name: 'Vivo Gaming' },
  { name: 'Winfinity' },
];

const sportsData = [
  { label: 'Cricket', img: '/images/sidebar/cricket.png' },
  { label: 'Soccer', img: '/images/sidebar/football.png' },
  { label: 'Tennis', img: '/images/sidebar/tennis.png' },
];

const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

export default function Sidebar({
  collapse,
  onToggle,
}: {
  collapse: boolean;
  onToggle: () => void;
}) {
  const { loggedIn } = useUserInfo();
  const [showSports, setShowSports] = useState(true);
  const [showCategories, setShowCategories] = useState(true);
  const [showProviders, setShowProviders] = useState(true);
  const [mode, setMode] = useState<'all' | 'casino' | 'sport'>('all');

  // Search state — drives the single API call
  const [eventSearch, setEventSearch] = useState('');

  // Open/close state for the tree (IDs only — data comes from the API)
  const [openSport, setOpenSport] = useState<string | null>(null);
  const [openCompetition, setOpenCompetition] = useState<string | null>(null);
  const [openEvent, setOpenEvent] = useState<string | null>(null);

  const navigate = useNavigate();
  const location = useLocation();

  const activeSportFromPath = location.pathname.startsWith('/sport/')
    ? (sportsData.find(
        (sport) =>
          sport.label.toLowerCase() === (location.pathname.split('/')[2] || '').toLowerCase()
      )?.label ?? '')
    : '';

  const selectedSport = openSport ?? (activeSportFromPath ? capitalize(activeSportFromPath) : null);

  const isSearching = eventSearch.trim().length > 0;

  // ── Single API call — omit sport filter while searching so all sports are returned ──
  const { data: treeData, isFetching: isTreeFetching } = useGetCompetitionsEventsMarketsQuery(
    {
      sport: isSearching ? undefined : (selectedSport ?? 'Cricket'),
      eventSearch: eventSearch || undefined,
    },
    {
      // While searching we always fetch (no sport required); otherwise need a selected sport
      skip: mode === 'casino' || (!isSearching && !selectedSport),
      refetchOnMountOrArgChange: true,
    }
  );

  // Normalised: keyed by sport name (as returned by the API)
  const sportTree: Record<string, CompetitionItem[]> = treeData?.data?.data ?? {};

  // Helper: get competitions for a sport label
  const getCompetitionsForSport = (sportLabel: string): CompetitionItem[] => {
    const key =
      Object.keys(sportTree).find((k) => k.toLowerCase() === sportLabel.toLowerCase()) ?? '';
    return sportTree[key] ?? [];
  };

  // While searching, a sport row is "open" if the API returned data for it
  const isSportOpen = (sportLabel: string) => {
    if (isSearching) return getCompetitionsForSport(sportLabel).length > 0;
    return openSport === sportLabel;
  };

  const { data: eventCountData } = useGetEventCountQuery({ isAuthenticated: Boolean(loggedIn) });
  const { data: sportsMeta } = useGetSportsMetaDetailsQuery();
  const sportCounts = sportsMeta?.fixture?.sportCounts ?? [];

  const getSportCount = (sportLabel: string) =>
    sportCounts.find((item) => item.sport?.toLowerCase() === sportLabel.toLowerCase())?.total ?? 0;

  // Reset nested state when sidebar collapses
  useEffect(() => {
    if (collapse) {
      setOpenSport(null);
      setOpenCompetition(null);
      setOpenEvent(null);
    }
  }, [collapse]);

  useEffect(() => {
    if (location.pathname.startsWith('/casino')) {
      setMode('casino');
      return;
    }
    if (location.pathname.startsWith('/sport')) {
      setMode('sport');
      return;
    }
    setMode('all');
  }, [location.pathname]);

  const showCategorySection = (mode === 'all' || mode === 'casino') && (collapse || showCategories);
  const showProviderSection = (mode === 'all' || mode === 'casino') && (collapse || showProviders);

  const handleCollapsedSportClick = (sportLabel: string) => {
    onToggle();
    setOpenSport(sportLabel);
    setOpenCompetition(null);
    setOpenEvent(null);
  };

  const handleSportClick = (sportLabel: string) => {
    if (openSport === sportLabel) {
      setOpenSport(null);
      setOpenCompetition(null);
      setOpenEvent(null);
    } else {
      setOpenSport(sportLabel);
      setOpenCompetition(null);
      setOpenEvent(null);
    }
  };

  const handleCompetitionClick = (competitionId: string) => {
    if (openCompetition === competitionId) {
      setOpenCompetition(null);
      setOpenEvent(null);
    } else {
      setOpenCompetition(competitionId);
      setOpenEvent(null);
    }
  };

  const handleEventClick = (eventId: string) => {
    if (openEvent === eventId) {
      setOpenEvent(null);
    } else {
      setOpenEvent(eventId);
    }
  };

  const isMenuItemActive = (path?: string) => (path ? location.pathname === path : false);

  const { supportInfo } = useAppSelector((state) => state.user);

  const { data } = useGetCasinoGamesCountQuery();
  const { data: favData } = useGetFavouriteCasinoGamesQuery(
    { page: 1, limit: 10 },
    { skip: !loggedIn }
  );
  const categories = [
    ...(loggedIn ? [{ category: 'Favourite', count: favData?.pagination?.totalItems ?? 0 }] : []),
    ...(data?.games ?? [])
      .slice()
      .sort((a, b) => a.category.localeCompare(b.category, undefined, { sensitivity: 'base' })),
  ];

  const [searchParams, setSearchParams] = useSearchParams();
  const [gameSearch, setGameSearch] = useState(searchParams.get('search') || '');

  useEffect(() => {
    setSearchParams((prev) => {
      if (gameSearch) {
        prev.set('search', gameSearch);
      } else {
        prev.delete('search');
      }
      return prev;
    });
  }, [gameSearch]);

  const counts = eventCountData?.count;
  const inplayCount = counts?.inplay ?? 0;
  const upcomingCount = counts?.total ?? 0;
  const favouriteCount = counts?.favorite ?? 0;

  const menuItems = [
    {
      label: 'In Play',
      img: '/images/sidebar/inplay.svg',
      count: inplayCount,
      path: '/sport/inplay-events',
    },
    {
      label: 'Upcoming',
      img: '/images/sidebar/upcoming.svg',
      count: upcomingCount,
      path: '/sport/upcoming-events',
    },
    {
      label: 'Favourites',
      path: !loggedIn ? '?mode=login' : '/sport/favourites',
      img: '/images/sidebar/favourite.svg',
      count: favouriteCount,
    },
  ];

  // FIX: close sidebar on mobile when navigating
  const isMobile = () => window.innerWidth < 1024;
  const isSmallScreen = () => window.innerWidth < 1536;
  const closeOnMobile = () => {
    if (isSmallScreen()) onToggle();
  };

  return (
    <div
      className={`max-lg:pb-[70px] lg:m-[10px] border border-[#2f3741] rounded-[10px] fixed left-0 bottom-0 bg-[#1C1D25]
      flex flex-col transition-all duration-300 z-[6000] lg:z-[999] top-11 lg:top-[34px] 2xl:top-0
      ${
        collapse
          ? 'w-0 -translate-x-full overflow-hidden lg:translate-x-0 lg:w-[60px]'
          : 'w-full lg:w-[300px] translate-x-0'
      }`}
    >
      <div className="flex-1 overflow-y-auto overscroll-contain scrollbar-hide pb-3">
        {/* ── Mode Buttons ── */}
        <div className={`flex ${collapse ? 'flex-col items-center gap-3 mt-3' : 'gap-2 p-3'}`}>
          {(['casino', 'sport'] as const).map((item) => (
            <NavLink
              key={item}
              to={`/${item}`}
              className={({ isActive }) =>
                `flex items-center justify-center gap-2 text-white text-[11px] font-semibold rounded-md transition
                ${isActive ? 'bg-primary' : 'bg-[#2F3741] hover:bg-primary'}
                ${collapse ? 'w-10 h-10' : 'flex-1 h-10'}`
              }
            >
              <img src={`/images/sidebar/${item}.svg`} alt={item} className="size-4" />
              {!collapse && item.charAt(0).toUpperCase() + item.slice(1)}
            </NavLink>
          ))}
        </div>

        {mode !== 'casino' && <div className="h-px bg-[#2F3741] my-2 mx-3" />}

        {/* ── Menu Items ── */}
        {mode !== 'casino' && (
          <div
            className={`${collapse ? 'flex flex-col items-center gap-3 mt-2' : 'px-3 space-y-1'}`}
          >
            {menuItems.map((item) =>
              collapse ? (
                <div
                  key={item.label}
                  title={item.label}
                  onClick={() => {
                    if (item.path) navigate(item.path);
                    onToggle();
                  }}
                  className={`w-10 h-10 flex items-center justify-center rounded-md cursor-pointer ${
                    isMenuItemActive(item.path) ? 'bg-primary' : ''
                  }`}
                >
                  <img src={item.img} alt={item.label} />
                </div>
              ) : (
                <div
                  key={item.label}
                  // FIX: also close sidebar on mobile for expanded menu items
                  onClick={() => {
                    if (item.path) {
                      navigate(item.path);
                      closeOnMobile();
                    }
                  }}
                  className={`flex items-center justify-between px-2 py-2 rounded-md hover:bg-gold-fade cursor-pointer h-[45px] ${
                    isMenuItemActive(item.path) ? 'bg-gold-fade' : ''
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <img src={item.img} alt={item.label} />
                    <span className="text-xs text-white">{item.label}</span>
                  </div>
                  <span className="text-[10px] text-gray-400">{item.count}</span>
                </div>
              )
            )}
          </div>
        )}

        {!collapse && <div className="h-px bg-[#2F3741] my-3 mx-3" />}

        {/* ── Sports Section ── */}
        {mode !== 'casino' && (
          <>
            {/* Collapsed: icons only */}
            {collapse && (
              <div className="flex flex-col items-center gap-3 mt-2">
                {sportsData.map((sport) => (
                  <div
                    key={sport.label}
                    title={sport.label}
                    onClick={() => handleCollapsedSportClick(sport.label)}
                    className="w-10 h-10 flex items-center justify-center rounded-md cursor-pointer transition hover:bg-gold-fade"
                  >
                    <img src={sport.img} alt={sport.label} />
                  </div>
                ))}
              </div>
            )}

            {/* Expanded */}
            {!collapse && (
              <div className="px-3">
                <div className="text-[11px] font-semibold uppercase mb-2 flex items-center justify-between">
                  <span className="text-[#cbcbcb]">Sports</span>
                  <div onClick={() => setShowSports(!showSports)} className="cursor-pointer">
                    {showSports ? <Icons.Up /> : <Icons.Down />}
                  </div>
                </div>

                {showSports && (
                  <>
                    {/* Competition search */}
                    <div className="flex items-center bg-[#2F3741] rounded-md px-2 h-9 mb-2">
                      <FaSearch size={12} className="text-gray-400 mr-2" />
                      <input
                        placeholder="Search Event"
                        value={eventSearch}
                        onChange={(e) => setEventSearch(e.target.value)}
                        className="bg-transparent outline-none text-xs text-white w-full"
                      />
                    </div>

                    <div className="space-y-1">
                      {sportsData.map((sport) => {
                        const competitions = getCompetitionsForSport(sport.label);

                        return (
                          <div key={sport.label}>
                            {/* ── Sport Row ── */}
                            <div
                              onClick={() => handleSportClick(sport.label)}
                              className="flex items-center justify-between px-2 py-2 cursor-pointer hover:bg-gold-fade rounded-md h-[45px]"
                            >
                              <div className="flex items-center gap-2">
                                <img src={sport.img} className="w-8" alt={sport.label} />
                                <span className="text-xs text-white">{sport.label}</span>
                              </div>
                              <div className="flex items-center gap-1 text-gray-400 text-[10px]">
                                <span>{getSportCount(sport.label)}</span>
                                {isSportOpen(sport.label) ? (
                                  <Icons.Up className="text-white" />
                                ) : (
                                  <Icons.Down className="text-white" />
                                )}
                              </div>
                            </div>

                            {/* ── Competitions ── */}
                            {isSportOpen(sport.label) && (
                              <div className="ml-3 space-y-1 pl-2">
                                {isTreeFetching ? (
                                  <div className="px-2 py-2 text-[11px] text-gray-400">
                                    Loading competitions...
                                  </div>
                                ) : competitions.length > 0 ? (
                                  competitions.map((comp) => (
                                    <div key={comp.id}>
                                      {/* ── Competition Row ── */}
                                      <div
                                        onClick={() => handleCompetitionClick(String(comp.id))}
                                        className="flex items-center justify-between px-2 py-2 cursor-pointer hover:bg-primary group hover:text-black rounded-md"
                                      >
                                        <div className="flex items-center gap-2">
                                          <span className="w-2 h-2 bg-white group-hover:bg-black rounded-full opacity-80 flex-shrink-0" />
                                          <span className="text-[11px] font-semibold leading-tight">
                                            {comp.name}
                                          </span>
                                        </div>
                                        {isSearching || openCompetition === String(comp.id) ? (
                                          <Icons.Up size={11} />
                                        ) : (
                                          <Icons.Down size={11} />
                                        )}
                                      </div>

                                      {/* ── Events ── */}
                                      {(isSearching || openCompetition === String(comp.id)) && (
                                        <div className="ml-3 space-y-1 pl-2">
                                          {comp.events.length > 0 ? (
                                            comp.events.map((event) => (
                                              <div key={event.id}>
                                                {/* ── Event Row ── */}
                                                <div
                                                  onClick={() => handleEventClick(event.id)}
                                                  className="flex items-center justify-between px-2 py-1 cursor-pointer hover:bg-primary group hover:text-black rounded-md"
                                                >
                                                  <div className="flex items-center gap-2">
                                                    <span className="w-2 h-2 border border-white group-hover:border-black rounded-full flex-shrink-0" />
                                                    <span className="text-[11px]">
                                                      {event.name}
                                                    </span>
                                                  </div>
                                                  {openEvent === event.id ? (
                                                    <Icons.Up size={11} />
                                                  ) : (
                                                    <Icons.Down size={11} />
                                                  )}
                                                </div>

                                                {/* ── Markets ── */}
                                                {openEvent === event.id && (
                                                  <div className="ml-5 space-y-1 pl-2">
                                                    {event.markets.length > 0 ? (
                                                      event.markets.map((market) => (
                                                        <div
                                                          key={market.id}
                                                          // FIX: close sidebar on mobile when navigating to a market
                                                          onClick={() => {
                                                            if (!loggedIn) {
                                                              navigate('?mode=login');
                                                              return;
                                                            }
                                                            navigate(
                                                              `/sport/event-details/${event.id}`
                                                            );
                                                            closeOnMobile();
                                                          }}
                                                          className="flex items-center gap-2 px-2 py-1 text-[11px] text-gray-300 hover:bg-primary hover:text-black rounded-md cursor-pointer"
                                                        >
                                                          <Icons.Check className="" />
                                                          {market.name}
                                                        </div>
                                                      ))
                                                    ) : (
                                                      <div className="px-2 py-1 text-[11px] text-gray-400">
                                                        No markets available.
                                                      </div>
                                                    )}
                                                  </div>
                                                )}
                                              </div>
                                            ))
                                          ) : (
                                            <div className="px-2 py-2 text-[11px] text-gray-400">
                                              No events available.
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  ))
                                ) : (
                                  <div className="px-2 py-2 text-[11px] text-gray-400">
                                    No competitions available.
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        )}

        {/* Casino mode search */}
        {!collapse && mode === 'casino' && (
          <div className="px-3 mb-2">
            <div className="flex items-center bg-[#2F3741] rounded-md px-2 h-9">
              <FaSearch size={12} className="text-gray-400 mr-2" />
              <input
                placeholder="Search Game"
                value={gameSearch}
                onChange={(e) => setGameSearch(e.target.value)}
                className="bg-transparent outline-none text-xs text-white w-full"
              />
            </div>
          </div>
        )}

        {showCategorySection && <div className="h-px bg-[#2F3741] my-3 mx-3" />}

        {/* ── Categories ── */}
        {(mode === 'all' || mode === 'casino') && (
          <div
            className={`${collapse ? 'flex flex-col items-center gap-3 mt-2' : 'px-3 space-y-1'}`}
          >
            {!collapse && (
              <div className="text-[11px] font-semibold uppercase mb-2 flex items-center justify-between">
                <span className="text-[#cbcbcb]">Categories</span>
                <div onClick={() => setShowCategories(!showCategories)} className="cursor-pointer">
                  {showCategories ? <Icons.Up /> : <Icons.Down />}
                </div>
              </div>
            )}

            {(collapse || showCategories) &&
              categories.map((item) =>
                collapse ? (
                  <Link
                    to={`/casino?category=${item.category}`}
                    key={item.category}
                    title={item.category}
                    onClick={onToggle}
                    className="w-10 h-10 flex items-center justify-center rounded-md bg-[#2F3741] cursor-pointer transition hover:bg-gold-fade"
                  >
                    <img
                      src={`/images/sidebar/category/${item.category}.png`}
                      alt={item.category}
                      className="w-5 h-5"
                    />
                  </Link>
                ) : (
                  <Link
                    to={`/casino?category=${item.category}`}
                    key={item.category}
                    // FIX: close sidebar on mobile when selecting a category
                    onClick={closeOnMobile}
                    className="flex items-center justify-between px-2 py-2 rounded-md hover:bg-gold-fade cursor-pointer transition h-[45px]"
                  >
                    <div className="flex items-center gap-2">
                      <img
                        src={`/images/sidebar/category/${item.category}.png`}
                        alt={item.category}
                        className="w-6"
                      />
                      <span className="text-xs text-white">{item.category}</span>
                    </div>
                    <span className="text-[10px] text-gray-400">{item.count}</span>
                  </Link>
                )
              )}
          </div>
        )}

        {showProviderSection && <div className="h-px bg-[#2F3741] my-3 mx-3" />}

        {/* ── Providers ── */}
        {(mode === 'all' || mode === 'casino') && (
          <div
            className={`${collapse ? 'flex flex-col items-center gap-3 mt-2' : 'px-3 space-y-1'}`}
          >
            {!collapse && (
              <div className="text-[11px] font-semibold text-[#cbcbcb] uppercase mb-2 flex items-center justify-between">
                <span>Providers</span>
                <div onClick={() => setShowProviders(!showProviders)} className="cursor-pointer">
                  {showProviders ? <Icons.Up /> : <Icons.Down />}
                </div>
              </div>
            )}

            {(collapse || showProviders) &&
              providers.map((item) =>
                collapse ? (
                  <Link
                    to={`/casino?provider=${item.name}`}
                    key={item.name}
                    title={item.name}
                    onClick={onToggle}
                    className="w-10 h-10 flex items-center justify-center rounded-md bg-[#2F3741] cursor-pointer transition hover:bg-primary"
                  >
                    <img
                      src={`/images/sidebar/provider/${item.name}.svg`}
                      alt={item.name}
                      className="w-5 h-5"
                    />
                  </Link>
                ) : (
                  <Link
                    to={`/casino?provider=${item.name}`}
                    key={item.name}
                    // FIX: close sidebar on mobile when selecting a provider
                    onClick={closeOnMobile}
                    className="flex items-center px-2 py-2 rounded-md hover:bg-gold-fade cursor-pointer transition gap-2 h-[45px]"
                  >
                    <img
                      src={`/images/sidebar/provider/${item.name}.svg`}
                      alt={item.name}
                      className="w-6"
                    />
                    <span className="text-xs text-white">{item.name}</span>
                  </Link>
                )
              )}
          </div>
        )}

        {/* ── Bottom Section ── */}
        <div className={`pb-5 ${collapse ? 'flex flex-col items-center gap-3 mt-3' : 'px-3'}`}>
          {collapse ? (
            <>
              <Link
                to="/referrals"
                title="Refer & Earn"
                onClick={onToggle}
                className="w-10 h-10 flex items-center justify-center rounded-md cursor-pointer bg-[#3a4450]"
              >
                <img src="/images/sidebar/refer.svg" className="w-5 h-5" />
              </Link>

              <Link
                to={supportInfo?.whatsapp ?? '#'}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => {
                  !supportInfo?.whatsapp && e.preventDefault();
                }}
                title="WhatsApp Chat"
                className="w-10 h-10 flex items-center justify-center rounded-md cursor-pointer bg-[#008A22] animate-pulsing"
              >
                <img src="/images/sidebar/whatsapp.svg" className="w-5 h-5" />
              </Link>
            </>
          ) : (
            <>
              <div className="my-2 bg-[#4B5563] h-px" />

              <Link onClick={closeOnMobile} to="/referrals" className="flex gap-2 h-10 mt-3 mb-2">
                <button className="flex-1 flex items-center justify-center gap-[10px] bg-[#2F3741] text-white text-xs font-bold py-2 rounded">
                  <img src="/images/sidebar/refer.svg" />
                  Refer & Earn
                </button>
              </Link>

              <div className="my-2 bg-[#4B5563] h-px" />

              <div className="my-[15px] flex justify-between text-[11px] text-[#cbcbcb] uppercase">
                <span>Settings</span>
              </div>

              <Link
                to={supportInfo?.whatsapp ?? '#'}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => {
                  !supportInfo?.whatsapp && e.preventDefault();
                  closeOnMobile();
                }}
                className="flex items-center bg-[#008A22] rounded-[5px] py-1 px-2 animate-pulsing"
              >
                <img src="/images/sidebar/whatsapp.svg" />
                <span className="p-[5px] text-white text-xs">WhatsApp Chat</span>
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
